import { useEffect, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";
import { fetchAllListItems, INITIATIVE_LIST_ID, LIBRARY_LIST_ID, mapInitiative, mapLibrary } from "../api/graph";
import type { InitiativeItem, LibraryItem } from "../types/navigator";

export type Contributions = { initiatives: InitiativeItem[]; library: LibraryItem[] };

// Shared for a few minutes so the dashboard and the Champions page don't
// each pull both lists again.
let cached: { at: number; data: Promise<Contributions> } | null = null;
const FRESH_MS = 5 * 60 * 1000;

/** Everything submitted to AI Initiatives and the AI Library, with who added it. */
export function useContributions(): { data: Contributions | null; error: string | null } {
  const getToken = useGraphToken();
  const [state, setState] = useState<{ data: Contributions | null; error: string | null }>({ data: null, error: null });

  useEffect(() => {
    let alive = true;
    if (!cached || Date.now() - cached.at > FRESH_MS) {
      const data = (async () => {
        const token = await getToken();
        const [initiatives, library] = await Promise.all([
          fetchAllListItems(token, INITIATIVE_LIST_ID, mapInitiative),
          fetchAllListItems(token, LIBRARY_LIST_ID, mapLibrary),
        ]);
        return { initiatives, library };
      })();
      cached = { at: Date.now(), data };
      data.catch(() => (cached = null));
    }
    cached.data.then(
      (data) => alive && setState({ data, error: null }),
      (err) => alive && setState({ data: null, error: err instanceof Error ? err.message : String(err) })
    );
    return () => {
      alive = false;
    };
  }, [getToken]);

  return state;
}
